'use client';
import { useTranslations } from 'next-intl';
import { OverflowGrid, OverflowGridItem } from '@//components/OverflowGrid';
import ProductCardContainer from '@//containers/ProductCardContainer';
import ProductCardSkeleton from '@//containers/ProductCardSkeleton';
import useRecentVehicles from '@//data/search/vehicles/useRecentVehicles';

const skeletonArray = new Array(3).fill(null);

interface MarketMetadata {
  currency: string;
  pageLinks: {
    itemPage: string;
  };
}

interface StartpageRecentVehiclesContentProps {
  marketMetadata: MarketMetadata;
}

const StartpageRecentVehiclesContent = ({
  marketMetadata,
}: StartpageRecentVehiclesContentProps) => {
  const t = useTranslations('pages.startpage.recentVehicles');
  const { recentVehicles, isLoading, hasRecentVehicles } = useRecentVehicles(6);

  // Don't render if no recent vehicles exist or still loading
  if (isLoading) {
    return (
      <OverflowGrid id="recent-vehicles-skeletons" columns={3} heading={t('heading')}>
        {skeletonArray.map((_, index) => (
          <OverflowGridItem key={index}>
            <div role="status" aria-live="polite" className="sr-only">
              Loading recent vehicles...
            </div>
            <ProductCardSkeleton />
          </OverflowGridItem>
        ))}
      </OverflowGrid>
    );
  }

  if (!hasRecentVehicles) {
    return null;
  }

  return (
    <OverflowGrid id="recent-vehicles" columns={3} heading={t('heading')}>
      {recentVehicles.map((vehicle, index) => (
        <OverflowGridItem key={vehicle._id}>
          <ProductCardContainer
            item={vehicle}
            index={index}
            itemPage={marketMetadata.pageLinks.itemPage}
            currency={marketMetadata.currency}
            testId={`recent-vehicle-${index}`}
            loading={index < 3 ? 'eager' : 'lazy'}
          />
        </OverflowGridItem>
      ))}
    </OverflowGrid>
  );
};

export default StartpageRecentVehiclesContent;
